import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Modal, Spin, Button, Divider, Empty } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTableCells, faTrash } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { formatUuidT } from "../../../flatbuffers/display";
import { updateOpenModalManageGrids } from "../../../redux/monitoring/monitoringSlice";
import {
  useLazyGetFavoriteGridsByUserQuery,
  useDeleteFavoriteGridsMutation,
} from "../../../redux/monitoring/monitoringAPI";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import { RootState } from "../../../app/store";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";

function MonitoringModalManageGrids() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [listFavoriteGrids, setListFavoriteGrids] = useState<any>([]);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { openModalManageGrids, idGridSelected } = useSelector(
    (state: RootState) => state.monitoring,
  );
  const { theme, userLogged } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetFavoriteGridsByUser, { data: dataGetFavoriteGrids, isLoading, isFetching }] =
    useLazyGetFavoriteGridsByUserQuery();
  const [
    deleteMonitorGrid,
    {
      isSuccess: isSuccessDeleteMonitorGrid,
      isError: isErrorDeleteMonitorGrid,
      error: errorPutMonitorGrid,
      isLoading: isLoadingDeleteMonitorGrid,
      reset: resetDeleteMonitorGrid,
    },
  ] = useDeleteFavoriteGridsMutation();
  // ************************************************ */
  // FUNCTIONS ************************************** */
  const initData = async () => {
    const token = await GETJwtToken();
    triggerGetFavoriteGridsByUser({ token, idUser: userLogged.id });
  };

  const onClickDeleteGrid = async (grid: any) => {
    if (formatUuidT(grid.id) === idGridSelected) return;
    const token = await GETJwtToken();
    deleteMonitorGrid({ id: formatUuidT(grid.id), token });
  };

  const renderGridList = () => {
    if (isLoading || isFetching) {
      return <Spin />;
    }

    if (!listFavoriteGrids || listFavoriteGrids.length === 0) {
      return <Empty description={t("monitoring.noGridsFound")} />;
    }

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "10px",
        }}
      >
        {(listFavoriteGrids ?? []).map((grid: any) => {
          const gridId = formatUuidT(grid.id);
          const isSelectedGrid = gridId === idGridSelected;

          return (
            <div
              key={gridId || grid.name}
              style={{
                borderRadius: "8px",
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minHeight: "44px",
                textAlign: "center",
              }}
              className={`generalStyles__box__${theme} organizationCascader__gridItem ${
                isSelectedGrid ? "organizationCascader__gridItem--selected" : ""
              }`}
              title={grid.name}
            >
              <span
                className="organizationCascader__gridItemText"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
              >
                {grid.name}
              </span>
              <Button
                danger
                type="text"
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={() => onClickDeleteGrid(grid)}
                disabled={isSelectedGrid}
              />
            </div>
          );
        })}
      </div>
    );
  };

  // ************************************************* */
  // USE EFFECT ************************************** */

  useEffect(() => {
    if (openModalManageGrids) {
      initData();
    }
  }, [openModalManageGrids]);

  useEffect(() => {
    if (dataGetFavoriteGrids && dataGetFavoriteGrids.data) {
      setListFavoriteGrids(dataGetFavoriteGrids.data);
    }
  }, [dataGetFavoriteGrids]);

  useEffect(() => {
    resetDeleteMonitorGrid();
  }, [isSuccessDeleteMonitorGrid, isErrorDeleteMonitorGrid]);
  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Modal
      closable={false}
      onCancel={() => {
        dispatch(updateOpenModalManageGrids(false));
      }}
      open={openModalManageGrids}
      footer={null}
      width={720}
    >
      <Spin spinning={isLoadingDeleteMonitorGrid}>
        <div className=" mt-4">
          {/** ************************************************************* */}
          {/** HEADER */}

          <div>
            <Divider orientation="left" className="generalStyles__drawerDivider">
              <h5>
                <FontAwesomeIcon
                  icon={faTableCells}
                  className="generalStyles__info generalStyles__mrFix"
                />
                {t("monitoring.manageGrids")}
              </h5>
            </Divider>
          </div>
          {/** ************************************************************* */}
          {/** LIST GRIDS SAVED */}
          <div style={{ minHeight: "120px" }}>{renderGridList()}</div>
          {/** ************************************************************* */}
          {/** CLOSE BUTTON */}
          <div className="generalStyles__flexEnd">
            <div />
            <div>
              <Button
                onClick={() => {
                  dispatch(updateOpenModalManageGrids(false));
                }}
                className="buttonStyle__8"
              >
                {t("general.close")}
              </Button>
            </div>
          </div>
        </div>
      </Spin>
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorDeleteMonitorGrid}
        isSuccess={isSuccessDeleteMonitorGrid}
        requestType="DELETE"
        error={errorPutMonitorGrid}
        name="Grid"
      />
    </Modal>
  );
}

export default MonitoringModalManageGrids;
